import { memo, type MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isSingularStationArea } from '@/modules/floorplanDesign/helpers';
import {
  getStationCarrierSide,
  getStationWidthForCarrierSide,
  type StationCarrierSide,
} from '@/modules/floorplanDesign/helpers/stationCapacity';
import { rotateConstellation } from '@/modules/floorplanDesign/helpers/rotateConstellation';
import { getLogicalPoiAngle, getStoredPoiAngle } from '@/modules/floorplanDesign/helpers/poiAngleHelpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';

function getSingleSelectedStationArea(selectedAreas: ReturnType<typeof useReadSelectedAreas>) {
  const areas = areasToFlatCollection(selectedAreas);
  if (areas.length !== 1) return null;

  const area = areas[0];
  if (!isSingularStationArea(area)) return null;
  if (!area.loadHandlingPoiSpecs[0]) return null;

  return area;
}

function CarrierSideComponent() {
  const { t } = useTranslation('interface');
  const selectedAreas = useReadSelectedAreas();
  const mode = useAppStore((state) => state.mode);
  const fddmService = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const stationArea = useMemo(() => getSingleSelectedStationArea(selectedAreas), [selectedAreas]);

  const loadType = useMemo(() => {
    if (!stationArea) return undefined;

    return fddmService.getLoadType(stationArea.stationSpec.loadTypeIds[0]);
  }, [fddmService, stationArea]);

  const selectedCarrierSide = useMemo<StationCarrierSide | undefined>(() => {
    if (!stationArea) return undefined;

    return getStationCarrierSide(stationArea, loadType);
  }, [loadType, stationArea]);

  const handleCarrierSideChange = useCallback(
    (_event: MouseEvent<HTMLElement>, nextSide: StationCarrierSide | null) => {
      if (!stationArea || !nextSide || nextSide === selectedCarrierSide) return;

      const loadHandlingPoiSpec = stationArea.loadHandlingPoiSpecs[0];
      if (!loadHandlingPoiSpec) return;

      if (loadHandlingPoiSpec.alwaysFitStation) {
        if (!loadType) return;

        fddmService.updateArea({
          ...stationArea,
          stationSpec: {
            ...stationArea.stationSpec,
            width: getStationWidthForCarrierSide(loadType, nextSide),
          },
        });

        return;
      }

      const currentPoiAngle = getLogicalPoiAngle(selectedCarrierSide ?? 'short');
      const nextPoiAngle = getLogicalPoiAngle(nextSide);
      const constellationAngleDelta = nextPoiAngle - currentPoiAngle;
      const nextStoredPoiAngle = getStoredPoiAngle(nextSide);

      fddmService.setPreference(
        'singularStationArea.loadHandlingPoiSpecs.angle',
        nextStoredPoiAngle,
      );

      fddmService.updateArea({
        ...stationArea,
        loadHandlingPoiSpecs: [
          {
            ...loadHandlingPoiSpec,
            angle: nextStoredPoiAngle,
          },
        ],
        constellationSpec: {
          ...stationArea.constellationSpec,
          angle: rotateConstellation(
            stationArea.constellationSpec.angle,
            constellationAngleDelta,
          ),
        },
      });
    },
    [fddmService, loadType, selectedCarrierSide, stationArea],
  );

  return (
    <PropertiesLabel i18nkey='interface:properties.area.load_carrier_orientation.label'>
      <ToggleButtonGroup
        value={selectedCarrierSide}
        exclusive
        onChange={handleCarrierSideChange}
        disabled={mode !== 'EDITABLE' || !stationArea}
      >
        <ToggleButton value='short' aria-label='Short'>
          {t('properties.area.load_carrier_orientation.short')}
        </ToggleButton>

        <ToggleButton value='long' aria-label='Long'>
          {t('properties.area.load_carrier_orientation.long')}
        </ToggleButton>
      </ToggleButtonGroup>
    </PropertiesLabel>
  );
}

export const CarrierSide = memo(CarrierSideComponent);
